import { useEffect, useState } from "react";
import { publicFileUrl } from "./publicCampaigns";
import { FOUNDER, BOARD, ADVISORS } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Stable key for a roster member: "Keith Sparks" → "keith-sparks". */
export const memberKey = (name) =>
  (name || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

/** Polaroid tape + monogram accent pairs, rotated for people added from the admin console. */
export const PALETTE = [
  { tape: "bg-yellow", accent: "text-cobalt" },
  { tape: "bg-blossom", accent: "text-grass" },
  { tape: "bg-grass", accent: "text-blossom" },
  { tape: "bg-cobalt", accent: "text-tangerine" },
  { tape: "bg-tangerine", accent: "text-cobalt" },
  { tape: "bg-scarlet", accent: "text-grass" },
];

/** Next palette pair after the last person in a group. */
export const nextPalette = (people) => {
  const last = people[people.length - 1]?.tape;
  const i = PALETTE.findIndex((p) => p.tape === last);
  return PALETTE[(i + 1) % PALETTE.length];
};

export const EMPTY_TEAM_DATA = { photos: {}, order: {} };

/** Admin overrides, admin-added members and drag order from the backend: `{photos, order}`. Empty until loaded or on error. */
export const useTeamData = () => {
  const [data, setData] = useState(EMPTY_TEAM_DATA);
  useEffect(() => {
    let alive = true;
    fetch(`${API}/team/photos`)
      .then((r) => (r.ok ? r.json() : EMPTY_TEAM_DATA))
      .then((d) => { if (alive) setData({ photos: d.photos || {}, order: d.order || {} }); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  return data;
};

/** Merge an override onto a static roster entry (static values stay when no override). */
export const withTeamPhotos = (person, photos) => {
  const o = photos[memberKey(person.name)];
  if (!o) return person;
  return {
    ...person,
    photoNow: o.now_file_id ? publicFileUrl(o.now_file_id, 960) : person.photoNow,
    photoThen: o.then_file_id ? publicFileUrl(o.then_file_id, 480) : person.photoThen,
    nowFocusY: o.now_file_id ? o.now_focus_y ?? 50 : person.nowFocusY,
    nowFocusX: o.now_file_id ? o.now_focus_x ?? 50 : person.nowFocusX,
    role: o.role || person.role,
    bio: o.bio || person.bio,
    hidden: Boolean(o.hidden),
  };
};

const customPerson = (o) => ({
  custom: true,
  name: o.name,
  role: o.role || "",
  bio: o.bio || "",
  school: o.school || "",
  tape: o.tape || PALETTE[0].tape,
  accent: o.accent || PALETTE[0].accent,
  photoNow: o.now_file_id ? publicFileUrl(o.now_file_id, 960) : undefined,
  photoThen: o.then_file_id ? publicFileUrl(o.then_file_id, 480) : undefined,
  nowFocusY: o.now_file_id ? o.now_focus_y ?? 50 : undefined,
  nowFocusX: o.now_file_id ? o.now_focus_x ?? 50 : undefined,
  hidden: Boolean(o.hidden),
});

/** People added from /admin/team for one group, oldest first (they sit after the built-in roster). */
export const customMembers = (photos, group) =>
  Object.values(photos)
    .filter((o) => o.custom && o.group === group && o.name)
    .sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""))
    .map(customPerson);

/** Apply the admin drag order: listed keys first in that order, everyone else after in default order. */
export const orderPeople = (people, keys = []) => {
  const rank = new Map(keys.map((k, i) => [k, i]));
  const listed = people.filter((p) => rank.has(memberKey(p.name))).sort((a, b) => rank.get(memberKey(a.name)) - rank.get(memberKey(b.name)));
  return [...listed, ...people.filter((p) => !rank.has(memberKey(p.name)))];
};

/** Full roster for /team from `{photos, order}`: built-in members with overrides, then admin-added people, in drag order; hidden ones dropped unless asked for. */
export const buildRoster = ({ photos = {}, order = {} } = EMPTY_TEAM_DATA, { includeHidden = false } = {}) => {
  const merge = (list, group) => {
    const all = orderPeople([...list.map((p) => withTeamPhotos(p, photos)), ...customMembers(photos, group)], order[group]);
    return includeHidden ? all : all.filter((p) => !p.hidden);
  };
  return {
    founder: withTeamPhotos(FOUNDER, photos),
    board: merge(BOARD, "board"),
    advisors: merge(ADVISORS, "advisor"),
  };
};

/** object-position for the "today" photo: admin vertical focus and/or a static horizontal nudge (`nowFocusX` in content.js). */
export const focusStyle = (person) =>
  person.nowFocusY == null && person.nowFocusX == null
    ? undefined
    : { objectPosition: `${person.nowFocusX ?? 50}% ${person.nowFocusY ?? 50}%` };
